/**
 * 2.0.0: 用户偏好服务
 *
 * 调用后端 /api/user/preferences 接口，实现偏好的 CRUD 操作。
 * 后端使用 Supabase service_role_key 直接操作 mem0_memories 表
 * （2.0.1 从 user_memories 迁移至 Mem0 实际使用的 mem0_memories），
 * 前端通过 auth_user_id 标识用户身份。
 *
 * 偏好来源:
 * - ai: Mem0 对话自动提取的偏好
 * - manual: 用户在 MyPage 手动添加的偏好
 *
 * 偏好类型:
 * - preference: 用户偏好（如"喜欢辣的"、"偏爱日料"）
 * - restriction: 限制条件（如"花生过敏"、"不吃猪肉"）
 * - fact: 事实信息（如"住在东京新宿"、"两个人旅行"）
 */

import { logger } from '../utils/logger';

/** 后端 API 基础 URL — 从环境变量获取 */
const BFF_URL = (import.meta.env.VITE_BFF_URL || 'http://localhost:8000').replace(/\/+$/, '');

/** 偏好类型枚举 */
export type PreferenceType = 'preference' | 'restriction' | 'fact';

/** 偏好来源枚举 */
export type PreferenceSource = 'ai' | 'manual';

/** 单条偏好数据结构 */
export interface Preference {
  id: string;
  text: string;
  type: PreferenceType;
  source: PreferenceSource;
  importance: number;
  created_at: string;
}

/** GET 接口返回的分组偏好数据 */
export interface PreferencesData {
  preferences: Preference[];
  restrictions: Preference[];
  facts: Preference[];
}

/**
 * 获取用户所有偏好，按类型分组返回。
 *
 * @param authUserId - 用户唯一标识
 * @returns 分组后的偏好数据
 */
export async function fetchPreferences(authUserId: string): Promise<PreferencesData> {
  try {
    const resp = await fetch(
      `${BFF_URL}/api/user/preferences?auth_user_id=${encodeURIComponent(authUserId)}`
    );
    if (!resp.ok) {
      throw new Error(`获取偏好失败: ${resp.status}`);
    }
    return await resp.json();
  } catch (error) {
    logger.error('获取用户偏好失败:', error);
    throw error;
  }
}

/**
 * 手动添加一条偏好。
 *
 * @param authUserId - 用户唯一标识
 * @param text - 偏好文本内容
 * @param type - 偏好类型
 * @returns 新创建的偏好记录
 */
export async function createPreference(
  authUserId: string,
  text: string,
  type: PreferenceType = 'preference'
): Promise<Preference> {
  try {
    const resp = await fetch(`${BFF_URL}/api/user/preferences`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ auth_user_id: authUserId, text, type }),
    });
    if (!resp.ok) {
      throw new Error(`添加偏好失败: ${resp.status}`);
    }
    return await resp.json();
  } catch (error) {
    logger.error('添加用户偏好失败:', error);
    throw error;
  }
}

/**
 * 编辑偏好文本内容。
 *
 * @param authUserId - 用户唯一标识
 * @param preferenceId - 偏好记录 ID
 * @param text - 新的偏好文本
 * @returns 更新后的偏好记录
 */
export async function updatePreference(
  authUserId: string,
  preferenceId: string,
  text: string
): Promise<Preference> {
  try {
    const resp = await fetch(`${BFF_URL}/api/user/preferences/${preferenceId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ auth_user_id: authUserId, text }),
    });
    if (!resp.ok) {
      throw new Error(`编辑偏好失败: ${resp.status}`);
    }
    return await resp.json();
  } catch (error) {
    logger.error('编辑用户偏好失败:', error);
    throw error;
  }
}

/**
 * 删除一条偏好。
 *
 * @param authUserId - 用户唯一标识
 * @param preferenceId - 偏好记录 ID
 */
export async function deletePreference(
  authUserId: string,
  preferenceId: string
): Promise<void> {
  try {
    const resp = await fetch(
      `${BFF_URL}/api/user/preferences/${preferenceId}?auth_user_id=${encodeURIComponent(authUserId)}`,
      { method: 'DELETE' }
    );
    if (!resp.ok) {
      throw new Error(`删除偏好失败: ${resp.status}`);
    }
  } catch (error) {
    logger.error('删除用户偏好失败:', error);
    throw error;
  }
}
